/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback, useRef, useEffect } from 'react';
import { ChatMessage, NetworkContext, AIAssistantState, VPCContext } from '../../../ai-assistant/types';
import { ContextBuilder } from '../../../ai-assistant/context-builder';
import { AnthropicProvider } from '../../../ai-assistant/providers/anthropic.provider';

interface TopologyIndex {
  providers: Array<{
    id: string;
    name: string;
    regions: Array<{
      id: string;
      name: string;
      accounts: Array<{
        id: string;
        name: string;
        discoveryFile: string;
      }>;
    }>;
  }>;
}

export function useAIAssistant(
  discoveryData: any,
  provider: string,
  region: string,
  accountId: string,
  topologyIndex: TopologyIndex | null
) {
  const [state, setState] = useState<AIAssistantState>({
    isOpen: false,
    isLoading: false,
    messages: [],
    context: null,
    error: null,
  });

  const [globalMode, setGlobalMode] = useState(false);
  const [globalContext, setGlobalContext] = useState<NetworkContext | null>(null);
  const [loadingGlobal, setLoadingGlobal] = useState(false);

  // Initialize provider once
  const llmProvider = useRef<AnthropicProvider | null>(null);

  const getProvider = useCallback(() => {
    if (!llmProvider.current) {
      try {
        llmProvider.current = new AnthropicProvider();
      } catch (error) {
        setState((s) => ({
          ...s,
          error: 'API key not configured. Check Anthropic API key.',
        }));
        return null;
      }
    }
    return llmProvider.current;
  }, []);

  // Load all discovery data when global mode is enabled
  useEffect(() => {
    if (!globalMode || !topologyIndex) {
      setGlobalContext(null);
      return;
    }

    let cancelled = false;
    setLoadingGlobal(true);

    (async () => {
      try {
        const allVpcs: VPCContext[] = [];
        let mainProvider = 'aws';
        let mainRegion = '';

        for (const prov of topologyIndex.providers) {
          for (const reg of prov.regions) {
            for (const acc of reg.accounts) {
              if (cancelled) return;

              try {
                const discoveryPath = `/data/${acc.discoveryFile}`;
                const res = await fetch(discoveryPath);
                if (res.ok) {
                  const data = await res.json();
                  let ctx: NetworkContext | null = null;

                  if (prov.id === 'aws') {
                    ctx = ContextBuilder.fromAWSDiscovery(data, reg.id, acc.id);
                  } else if (prov.id === 'oci') {
                    ctx = ContextBuilder.fromOCIDiscovery(data, reg.id, acc.id);
                  }

                  if (ctx && ctx.vpcs.length > 0) {
                    // Add account/region info to each VPC for context
                    const vpcsWithMeta = ctx.vpcs.map((vpc) => ({
                      ...vpc,
                      name: `[${acc.name}/${reg.id}] ${vpc.name}`,
                    }));
                    allVpcs.push(...vpcsWithMeta);

                    if (!mainRegion) {
                      mainProvider = prov.id;
                      mainRegion = reg.id;
                    }
                  }
                }
              } catch (e) {
                console.warn(`Failed to load discovery for ${acc.name}:`, e);
              }
            }
          }
        }

        if (!cancelled) {
          const combinedContext: NetworkContext = {
            provider: mainProvider as 'aws' | 'oci' | 'azure' | 'gcp',
            region: 'GLOBAL (multiple regions)',
            accountId: 'GLOBAL (multiple accounts)',
            vpcs: allVpcs,
          };
          setGlobalContext(combinedContext);
          setLoadingGlobal(false);
        }
      } catch (e) {
        console.error('Failed to load global context:', e);
        if (!cancelled) {
          setLoadingGlobal(false);
          setState((s) => ({ ...s, error: 'Failed to load global context' }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [globalMode, topologyIndex]);

  // Build context when discovery data changes
  const buildContext = useCallback((): NetworkContext | null => {
    // Use global context if in global mode
    if (globalMode && globalContext) {
      return globalContext;
    }

    if (!discoveryData) return null;

    if (provider === 'aws') {
      return ContextBuilder.fromAWSDiscovery(discoveryData, region, accountId);
    } else if (provider === 'oci') {
      return ContextBuilder.fromOCIDiscovery(discoveryData, region, accountId);
    }
    return null;
  }, [discoveryData, provider, region, accountId, globalMode, globalContext]);

  const sendMessage = useCallback(
    async (content: string) => {
      const llm = getProvider();
      if (!llm) return;

      if (globalMode && loadingGlobal) {
        setState((s) => ({
          ...s,
          error: 'Still loading global context, please wait...',
        }));
        return;
      }

      const context = buildContext();
      if (!context) {
        setState((s) => ({
          ...s,
          error: globalMode
            ? 'No global context available. Try disabling global mode.'
            : 'No topology context available. Select a topology first.',
        }));
        return;
      }

      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content,
        timestamp: new Date(),
      };

      setState((s) => ({
        ...s,
        isLoading: true,
        messages: [...s.messages, userMessage],
        context,
        error: null,
      }));

      try {
        const allMessages = [...state.messages, userMessage];
        const response = await llm.chat(allMessages, context);

        const assistantMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: response,
          timestamp: new Date(),
        };

        setState((s) => ({
          ...s,
          isLoading: false,
          messages: [...s.messages, assistantMessage],
        }));
      } catch (error) {
        console.error('AI Assistant error:', error);
        setState((s) => ({
          ...s,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to get response from AI',
        }));
      }
    },
    [state.messages, buildContext, getProvider, globalMode, loadingGlobal]
  );

  const toggleOpen = useCallback(() => {
    setState((s) => ({ ...s, isOpen: !s.isOpen }));
  }, []);

  const clearChat = useCallback(() => {
    setState((s) => ({ ...s, messages: [], error: null }));
  }, []);

  const toggleGlobalMode = useCallback(() => {
    setGlobalMode((prev) => !prev);
  }, []);

  return {
    ...state,
    sendMessage,
    toggleOpen,
    clearChat,
    globalMode,
    toggleGlobalMode,
    loadingGlobal,
    vpcCount: globalMode ? globalContext?.vpcs.length ?? 0 : buildContext()?.vpcs.length ?? 0,
  };
}
